import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, FileText } from 'lucide-react';
import CrmCoreListPage from './components/CrmCoreListPage';

const ContactsPage = () => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.crm.pages.contactsPage.${key}`, opts);

  const statusOptions = useMemo(() => [
    { value: 'active', label: p('status_active') },
    { value: 'prospect', label: p('status_prospect') },
    { value: 'client', label: p('status_client') },
    { value: 'inactive', label: p('status_inactive') },
  ], [t]);

  const sourceOptions = useMemo(() => [
    { value: 'website', label: p('source_website') },
    { value: 'referral', label: p('source_referral') },
    { value: 'lead_conversion', label: p('source_lead') },
    { value: 'event', label: p('source_event') },
  ], [t]);

  const positionOptions = useMemo(() => [
    { value: 'ceo', label: p('position_ceo') },
    { value: 'manager', label: p('position_manager') },
    { value: 'procurement', label: p('position_procurement') },
    { value: 'finance', label: p('position_finance') },
    { value: 'other', label: p('position_other') },
  ], [t]);

  return (
    <CrmCoreListPage
      pageKey="contactsPage"
      apiPath="/crm/contacts"
      icon={Users}
      entityRef={{ field: 'contact_ref', prefixKey: 'contact', fallback: 'CON' }}
      statusOptions={statusOptions}
      mapRecord={(r) => {
        const pos = positionOptions.find((o) => o.value === r.position);
        return {
          ...r,
          full_name: `${r.first_name || ''} ${r.last_name || ''}`.trim(),
          position_label: pos?.label || r.position || '—',
        };
      }}
      buildPayload={(form) => ({
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        phone: form.phone,
        company: form.company,
        position: form.position,
        source: form.source || 'website',
        status: form.status || 'active',
        rating: parseInt(form.rating, 10) || 3,
        notes: form.notes || '',
      })}
      formTabs={[
        { id: 'general', label: p('tab_general', { defaultValue: 'عام' }), icon: Users },
        { id: 'notes', label: p('tab_notes', { defaultValue: 'ملاحظات' }), icon: FileText, iconClassName: 'text-purple-400' },
      ]}
      fields={[
        { name: 'first_name', labelKey: 'field_first_name', required: true, placeholderKey: 'placeholder_first_name', tab: 'general' },
        { name: 'last_name', labelKey: 'field_last_name', required: true, placeholderKey: 'placeholder_last_name', tab: 'general' },
        { name: 'company', labelKey: 'field_company', placeholderKey: 'placeholder_company', tab: 'general' },
        { name: 'position', labelKey: 'field_position', type: 'select', options: positionOptions, tab: 'general' },
        { name: 'email', labelKey: 'field_email', inputType: 'email', placeholderKey: 'placeholder_email', tab: 'general' },
        { name: 'phone', labelKey: 'field_phone', inputType: 'tel', tab: 'general' },
        { name: 'status', labelKey: 'field_status', type: 'select', options: statusOptions, defaultValue: 'active', tab: 'general' },
        { name: 'source', labelKey: 'field_source', type: 'select', options: sourceOptions, defaultValue: 'website', tab: 'general' },
        { name: 'rating', labelKey: 'field_rating', inputType: 'number', defaultValue: '3', placeholderKey: 'placeholder_rating', tab: 'general' },
        { name: 'notes', labelKey: 'field_notes', type: 'textarea', fullWidth: true, tab: 'notes' },
      ]}
      columns={[
        { key: 'contact_ref', labelKey: 'col_ref' },
        { key: 'full_name', labelKey: 'col_name' },
        { key: 'company', labelKey: 'col_company' },
        { key: 'position_label', labelKey: 'col_position' },
        { key: 'phone', labelKey: 'col_phone', type: 'phone' },
        { key: 'email', labelKey: 'col_email' },
        { key: 'status', labelKey: 'col_status', type: 'status' },
        { key: 'created_at', labelKey: 'col_created', type: 'date' },
      ]}
    />
  );
};

export default ContactsPage;
